import { forwardRef, useId, useImperativeHandle, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperclipIcon } from '@phosphor-icons/react/dist/csr/Paperclip';

import styles from './FileField.module.css';

/**
 * A file picker that looks and lines up like every other field: the label
 * above (the `Field` label), then ONE control row — a "Choose file" face
 * drawn as the secondary button, and the chosen file's name beside it.
 *
 * The native `<input type="file">` is still THE control (keyboard, focus,
 * the accessible name, the form value): it is visually hidden, and a click
 * on the button face opens it. The face is decorative to assistive
 * technology (the input itself is focused and announced), so the input's
 * name is the field label alone. Why not the bare input, themed by `::file-selector-button` in
 * reset.css? Its own "No file chosen" text is the BROWSER's, in the
 * browser's language — English on an Arabic screen — and it sat on a
 * different baseline from the field label beside it, with the screen's own
 * file-name line repeating it a second time.
 */
export interface FileFieldProps {
  label: string;
  accept?: string;
  required?: boolean;
  disabled?: boolean;
  /** The chosen file's name, or `null` before one is chosen. */
  fileName: string | null;
  /** Shown while `fileName` is null, e.g. "No file chosen." */
  emptyText: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  className?: string;
}

export const FileField = forwardRef<HTMLInputElement, FileFieldProps>(function FileField(
  { label, accept, required = false, disabled = false, fileName, emptyText, onChange, id, className },
  ref,
) {
  const { t } = useTranslation();
  const generated = useId();
  const inputId = id ?? `${generated}file`;
  const nameId = `${inputId}-name`;
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  return (
    <div className={[styles['field'], className].filter(Boolean).join(' ')}>
      <label className={styles['label']} htmlFor={inputId}>
        {label}
        {required ? (
          <span className={styles['required']} aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      <div className={styles['control']}>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          disabled={disabled}
          aria-required={required || undefined}
          aria-describedby={nameId}
          className={styles['input']}
          onChange={onChange}
        />
        {/* The mouse target. Not a second <label> (it would join the input's
            accessible name) and not a <button> (the input is already the
            focusable control): a keyboard user tabs to the input itself. */}
        <span
          className={styles['face']}
          aria-hidden="true"
          onClick={() => {
            if (!disabled) inputRef.current?.click();
          }}
        >
          <PaperclipIcon size={16} />
          {t('actions.chooseFile')}
        </span>
        <span id={nameId} className={fileName === null ? styles['empty'] : styles['name']}>
          {fileName ?? emptyText}
        </span>
      </div>
    </div>
  );
});
